import { buildRecipientSources, defaultRecipientIds } from '../../../carriers/bolRecipients';

// A BOL can only go out from these statuses, and only with a carrier assigned.
export const BOL_STATUSES = ['APPROVED', 'ASSIGNED', 'READY_FOR_PICKUP'];

export function canSendBol(row: any): boolean {
  return BOL_STATUSES.includes(row?.status) && Boolean(row?.carrier);
}

// Each load's default BOL recipients, mirroring the single-load dialog's initial
// selection (assigned contact → dispatcher → first on the carrier record).
export function defaultBolEmails(row: any): string[] {
  const sources = buildRecipientSources(
    row?.carrier?.contacts,
    row?.carrier?.emails,
    row?.driverContactId ?? null
  );
  const ids = new Set(defaultRecipientIds(sources));
  return sources.filter((source) => ids.has(source.id)).map((source) => source.email);
}

export interface SendBolPlan {
  eligible: { row: any; emails: string[] }[];
  skipped: { reason: string; rows: any[] }[];
}

// Server-side a BOL also needs a pickup date and at least one recipient, so
// pre-filter those into skip buckets rather than letting the fan-out 400.
export function planSendBol(rows: any[]): SendBolPlan {
  const eligible: SendBolPlan['eligible'] = [];
  const notEligible: any[] = [];
  const noPickupDate: any[] = [];
  const noRecipient: any[] = [];

  for (const row of rows || []) {
    if (!canSendBol(row)) {
      notEligible.push(row);
      continue;
    }
    if (!row?.pickupDate) {
      noPickupDate.push(row);
      continue;
    }
    const emails = defaultBolEmails(row);
    if (!emails.length) {
      noRecipient.push(row);
      continue;
    }
    eligible.push({ row, emails });
  }

  const skipped: SendBolPlan['skipped'] = [];
  if (notEligible.length) {
    skipped.push({
      reason: 'not approved/assigned/ready, or no carrier assigned',
      rows: notEligible,
    });
  }
  if (noPickupDate.length) {
    skipped.push({ reason: 'no pickup date yet', rows: noPickupDate });
  }
  if (noRecipient.length) {
    skipped.push({ reason: 'the carrier has no email on file', rows: noRecipient });
  }

  return { eligible, skipped };
}
